import { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronsUpDownIcon, X } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-public";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type OrganisationUserResponse = components["schemas"]["OrganisationUserResponse"];

const USER_FETCH_LIMIT = 200;

export interface BillReviewerMultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  dirty?: boolean;
  placeholder?: string;
}

export function BillReviewerMultiSelect({
  value,
  onChange,
  disabled = false,
  dirty = false,
  placeholder = "Select reviewers...",
}: BillReviewerMultiSelectProps) {
  const api = useDolfinClient();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<OrganisationUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    setLoading(true);
    api
      .GET("/v1/users", { params: { query: { Limit: USER_FETCH_LIMIT } } })
      .then(({ data }) => setUsers(data?.data ?? []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [api]);

  const selectedSet = new Set(value);
  const selectedUsers = value.map(
    (id) => users.find((u) => u.id === id) ?? ({ id, email: id, role: "Member" } as OrganisationUserResponse),
  );

  function toggle(userId: string) {
    if (selectedSet.has(userId)) {
      onChange(value.filter((id) => id !== userId));
    } else {
      onChange([...value, userId]);
    }
  }

  function userLabel(u: OrganisationUserResponse): string {
    return u.name?.trim() || u.email;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-auto min-h-9 w-full justify-between font-normal",
            dirty && "ring-2 ring-amber-500/50",
          )}
        >
          <div className="flex flex-wrap gap-1 py-0.5">
            {selectedUsers.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedUsers.map((u) => (
                <Badge
                  key={u.id}
                  variant="secondary"
                  className="gap-1 pr-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span>{userLabel(u)}</span>
                  {/* A span, not a button: the chip sits inside the trigger button, and a button cannot nest in one. */}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${userLabel(u)}`}
                    className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (u.id) toggle(u.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      e.stopPropagation();
                      if (u.id) toggle(u.id);
                    }}
                  >
                    <X className="size-3" />
                  </span>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder="Search users..." />
          <CommandList>
            <CommandEmpty>{loading ? "Loading users..." : "No users found."}</CommandEmpty>
            <CommandGroup>
              {users.map((u) => {
                if (!u.id) return null;
                const isSelected = selectedSet.has(u.id);
                return (
                  <CommandItem
                    key={u.id}
                    value={`${u.name ?? ""} ${u.email}`}
                    onSelect={() => toggle(u.id!)}
                  >
                    <CheckIcon className={cn("mr-2 size-4", isSelected ? "opacity-100" : "opacity-0")} />
                    <span className="flex flex-col">
                      <span>{userLabel(u)}</span>
                      {u.name && <span className="text-xs text-muted-foreground">{u.email}</span>}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">{u.role}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {users.length === USER_FETCH_LIMIT && (
              <p className="px-2 py-1.5 text-xs text-muted-foreground border-t">
                Showing first {USER_FETCH_LIMIT} users. Refine search to narrow results.
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
